import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { SharedModuleV4 } from '../../../../shared/sharedv4.module';
import { FormButtonGroupSimpleComponent } from '../../../shared/form-button-group-simple/form-button-group-simple.component';
import { FormFieldCurrencyComponent } from '../../../shared/form-field-currency/form-field-currency.component';
import { FormFieldSelectComponent, SelectListType } from '../../../shared/form-field-select/form-field-select.component';
import { BaseStepComponent } from '../base-step/base-step.component';
import { CreditFacilityForm, FinanceCommitmentForm, LiabilitiesForm, VehicleIndividualForm } from '../../../../types/v4/vehicleIndividualSteps';
import { UserAction } from '../../../../api/v4/models/CommonUtilities/user-action';
import { OtherFinanceCommitmentType } from '../../../../api/v4/models/Models/v4/Application/FinancialDetails/other-finance-commitment-type';
import { pairwise, startWith, Subscription } from 'rxjs';
import { PeriodTypeEnum } from '../../../../api/v4/models/Models/v4/Application/FinancialDetails/period-type-enum';
import { CreditFacilityType } from '../../../../api/v4/models/Models/v4/Application/FinancialDetails/credit-facility-type';
import { numericRangeValidator } from '../../../../types/validators';
import { MaritalStatusEnum } from '../../../../api/v4/models/Models/v4/Application/PersonalDetails/marital-status-enum';
import { VehicleAppService } from '../../../../services/v4/vehicle-app.service';
import { FormFieldComponent } from '../../../shared/form-field/form-field.component';
import { LivingSituationEnum } from '../../../../api/v4/models/Models/v4/Application/PersonalDetails/living-situation-enum';

@Component({
  selector: 'app-liabilities',
  standalone: true,
  imports: [
    CommonModule,
    SharedModuleV4,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSnackBarModule,
    MatCardModule,
    MatButtonToggleModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatOptionModule,
    FormButtonGroupSimpleComponent,
    FormFieldSelectComponent,
    MatIconModule,
    FormFieldCurrencyComponent,
    FormFieldComponent
  ],
  templateUrl: './liabilities.component.html',
  styleUrl: './liabilities.component.scss'
})
export class LiabilitiesComponent extends BaseStepComponent {

  @Input() parentStepper!: MatStepper;
  @Input() fg!: FormGroup<LiabilitiesForm>;
  @Input() formSubmitted!: boolean;

  UserAction = UserAction;
  SelectListType = SelectListType;
  OtherFinanceCommitmentType = OtherFinanceCommitmentType;

  private livingPaymentAmountSubscription: Subscription | null = null;
  private livingPaymentFrequencySubscription: Subscription | null = null;
  individualHeaderDisplay: string = '';

  hasPartner: boolean = false;


  constructor(vehicleAppService: VehicleAppService) {
    super(vehicleAppService);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.fg.controls.hasCreditFacilities.valueChanges
      .pipe(
        startWith(this.fg.controls.hasCreditFacilities.value), // Emit the initial value
        pairwise() // Emit the previous and current values as an array
      )
      .subscribe(([oldValue, newValue]) => {
        if (newValue && newValue != oldValue) {
          if (newValue == UserAction.No) {
            this.fg.controls.creditFacilities.clear();
          } else {
            if (this.fg.controls.creditFacilities.length === 0) {
              this.addCreditFacility();
            }
          }
        }
      });

    this.fg.controls.hasOtherFinancialCommitments.valueChanges
      .pipe(
        startWith(this.fg.controls.hasOtherFinancialCommitments.value), // Emit the initial value
        pairwise() // Emit the previous and current values as an array
      )
      .subscribe(([oldValue, newValue]) => {
        if (newValue && newValue != oldValue) {
          if (newValue == UserAction.No) {
            this.fg.controls.financeCommitments.clear();
          } else {
            if (this.fg.controls.financeCommitments.length === 0) {
              this.addFinanceCommitment();
            }
          }
        }
      });

    /*
        this.subscriptions.push(this.parentFg.controls.step2.controls.maritalStatus.valueChanges.subscribe((value: any) => {
    
          this.hasPartner = (value == MaritalStatusEnum.Married || value == MaritalStatusEnum.DeFacto);
    
          this.fg.controls.creditFacilities.controls.forEach((cf: FormGroup<CreditFacilityForm>) => {
            if (this.hasPartner) {
              cf.controls.repaymentsSharedWithPartner.enable();
            }
            else {
              cf.controls.repaymentsSharedWithPartner.setValue(null);
              cf.controls.repaymentsSharedWithPartner.disable();
            }
          });
    
          this.fg.controls.financeCommitments.controls.forEach((loan: FormGroup<FinanceCommitmentForm>) => {
            if (this.hasPartner) {
              loan.controls.repaymentsSharedWithPartner.enable();
            }
            else {
              loan.controls.repaymentsSharedWithPartner.setValue(null);
              loan.controls.repaymentsSharedWithPartner.disable();
            }
          });
        }));
        */

    this.subscriptions.push(this.vehicleAppService.indivIndividualSubject$.subscribe((fgValue: FormGroup<VehicleIndividualForm> | null) => {
      console.log('Individual form group updated:', fgValue);

      // Check first individual's marital status to determine if partner fields should be enabled
      fgValue?.controls.step2.controls.maritalStatus.valueChanges.subscribe((value: any) => {
        console.log('Marital status changed to:', value);

        // if it is not a joint application, and married or de facto, then has partner is true
        // and need to ask for shared expenses and partner net income
        this.hasPartner = !this.vehicleAppService.isJointApplication() &&
          (value == MaritalStatusEnum.Married || value == MaritalStatusEnum.DeFacto);

        this.fg.controls.creditFacilities.controls.forEach((cf: FormGroup<CreditFacilityForm>) => {
          if (this.hasPartner) {
            cf.controls.repaymentsSharedWithPartner.enable();
          }
          else {
            cf.controls.repaymentsSharedWithPartner.setValue(null);
            cf.controls.repaymentsSharedWithPartner.disable();
          }
        });

        this.fg.controls.financeCommitments.controls.forEach((loan: FormGroup<FinanceCommitmentForm>) => {
          if (this.hasPartner) {
            loan.controls.repaymentsSharedWithPartner.enable();
          }
          else {
            loan.controls.repaymentsSharedWithPartner.setValue(null);
            loan.controls.repaymentsSharedWithPartner.disable();
          }
        });
      });

      // Add the disabled home loan if the user has a mortgage
      fgValue?.controls.step1.controls.addressPhysical.controls.livingSituation.valueChanges
        .pipe(
          startWith(fgValue?.controls.step1.controls.addressPhysical.controls.livingSituation.value), // Emit the initial value
          pairwise() // Emit the previous and current values as an array
        )
        .subscribe(([oldValue, newValue]: [LivingSituationEnum | null, LivingSituationEnum | null]) => {
          if (newValue && newValue != oldValue) {

            console.log('Living situation changed to:', newValue);
            var existingExpenseIndex = this.fg.controls.financeCommitments.controls.findIndex(x => x.controls.type.value === OtherFinanceCommitmentType.HomeLoan);

            if (newValue === LivingSituationEnum.Mortgaged) {
              if (existingExpenseIndex === -1) {
                this.addFinanceCommitment(OtherFinanceCommitmentType.HomeLoan);
                existingExpenseIndex = this.fg.controls.financeCommitments.controls.length - 1;
              }

              // get the expense by index
              var expense = this.fg.controls.financeCommitments.controls[existingExpenseIndex];
              expense.controls.paymentPeriod.setValue(fgValue?.controls.step1.controls.livingPaymentFrequency.value);
              expense.controls.paymentPeriod.disable();
              expense.controls.amountPerPeriod.setValue(fgValue?.controls.step1.controls.livingPaymentAmount.value);
              expense.controls.amountPerPeriod.disable();

              this.fg.controls.hasOtherFinancialCommitments.setValue(UserAction.Yes);
              this.fg.controls.hasOtherFinancialCommitments.disable();

              this.setupLivingPaymentListeners(fgValue);

            } else {
              this.removeLivingPaymentListeners();
              if (existingExpenseIndex !== -1) {
                this.removeFinanceCommitment(existingExpenseIndex);
                this.fg.controls.hasOtherFinancialCommitments.enable();

                if (this.fg.controls.financeCommitments.length === 0) {
                  this.fg.controls.hasOtherFinancialCommitments.setValue(UserAction.No);
                }
              }
            }
          }
        });
    }));
  }

  setupLivingPaymentListeners(fgValue: FormGroup<VehicleIndividualForm>): void {
    // Listen for the living payment frequency changes    
    const livingPaymentFrequencyControl = fgValue.controls.step1.controls.livingPaymentFrequency;
    this.livingPaymentFrequencySubscription = livingPaymentFrequencyControl.valueChanges.subscribe((value: any) => {
      var expense = this.fg.controls.financeCommitments.controls.find(x => x.controls.type.value === OtherFinanceCommitmentType.HomeLoan);
      if (!expense) {
        return;
      }
      // Handle the value change
      expense.controls.paymentPeriod.enable();
      expense.controls.paymentPeriod.setValue(fgValue.controls.step1.controls.livingPaymentFrequency.value);
      expense.controls.paymentPeriod.disable();
    });

    // Listen for the living payment amount changes
    const livingPaymentAmountControl = fgValue.controls.step1.controls.livingPaymentAmount;
    this.livingPaymentAmountSubscription = livingPaymentAmountControl.valueChanges.subscribe((value: any) => {
      var expense = this.fg.controls.financeCommitments.controls.find(x => x.controls.type.value === OtherFinanceCommitmentType.HomeLoan);
      if (!expense) {
        return;
      }
      // Handle the value change
      expense.controls.amountPerPeriod.enable();
      expense.controls.amountPerPeriod.setValue(fgValue.controls.step1.controls.livingPaymentAmount.value);
      expense.controls.amountPerPeriod.disable();
    });
  }

  removeLivingPaymentListeners(): void {
    if (this.livingPaymentFrequencySubscription) {
      this.livingPaymentFrequencySubscription.unsubscribe();
      this.livingPaymentFrequencySubscription = null;
    }

    if (this.livingPaymentAmountSubscription) {
      this.livingPaymentAmountSubscription.unsubscribe();
      this.livingPaymentAmountSubscription = null;
    }
  }

  removeCreditFacility(index: number): void {
    this.fg.controls.creditFacilities.removeAt(index);
    if (index == 0 && this.fg.controls.creditFacilities.length == 0) {
      this.fg.controls.hasCreditFacilities.setValue(UserAction.No);
    }
  }

  addCreditFacility(facilityType: CreditFacilityType | null = null): void {
    this.fg.controls.creditFacilities.push(this.createCreditFacilityGroup(facilityType));
  }

  createCreditFacilityGroup(facilityType: CreditFacilityType | null = null): FormGroup<CreditFacilityForm> {
    var fg = new FormGroup<CreditFacilityForm>({
      balance: new FormControl<number | null>(null, Validators.compose([Validators.required, numericRangeValidator(0, 1000000)])),
      type: new FormControl<CreditFacilityType | null>({ value: facilityType, disabled: facilityType != null }, Validators.required),
      provider: new FormControl<string | null>(null, Validators.required),
      creditLimit: new FormControl<number | null>(null, Validators.compose([Validators.required, numericRangeValidator(0, 1000000)])),
      repaymentsSharedWithPartner: new FormControl<UserAction | null>({ value: null, 'disabled': !this.hasPartner }, Validators.required),
      isPaidInFullEachMonth: new FormControl<UserAction | null>({ value: null, 'disabled': true }, Validators.required),
    } as CreditFacilityForm);

    this.subscriptions.push(fg.controls.type.valueChanges.subscribe((value: any) => {

      var isPaidInFullEachMonthControl = fg.controls.isPaidInFullEachMonth;
      if (value == CreditFacilityType.CreditCard || value == CreditFacilityType.BuyNowPayLater) {
        isPaidInFullEachMonthControl.enable();
      }
      else {
        isPaidInFullEachMonthControl.setValue(null);
        isPaidInFullEachMonthControl.disable();
      }
    }));

    return fg;
  }

  removeFinanceCommitment(index: number): void {
    this.fg.controls.financeCommitments.removeAt(index);
    if (index == 0 && this.fg.controls.financeCommitments.length == 0) {
      this.fg.controls.hasOtherFinancialCommitments.setValue(UserAction.No);
    }
  }

  addFinanceCommitment(type: OtherFinanceCommitmentType | null = null): FormGroup<FinanceCommitmentForm> {
    var fg = this.createFinanceCommitmentGroup(type);
    this.fg.controls.financeCommitments.push(fg);
    return fg;
  }

  createFinanceCommitmentGroup(type: OtherFinanceCommitmentType | null = null): FormGroup<FinanceCommitmentForm> {
    var fg = new FormGroup<FinanceCommitmentForm>({
      type: new FormControl<OtherFinanceCommitmentType | null>({ value: type, disabled: type != null }, Validators.required),
      provider: new FormControl<string | null>(null, Validators.required),
      balance: new FormControl<number | null>(null, Validators.required),
      amountPerPeriod: new FormControl<number | null>(null, Validators.required),
      paymentPeriod: new FormControl<PeriodTypeEnum | null>(null, Validators.required),
      repaymentsSharedWithPartner: new FormControl<UserAction | null>({ value: null, 'disabled': !this.hasPartner }, Validators.required),
    } as FinanceCommitmentForm);

    return fg;
  }

}
